/* Luren video grid. Every visible tile cycles its own set of three clips. A clip sits in at most two sets, two sets
   never share more than one clip, a repeated clip avoids neighbouring tiles, and no clip is on screen twice at once.
   Two rhythms (section setting): 'together' loads every first clip, starts them on the same frame and cuts all tiles
   on one shared beat; 'staggered' opens each tile's first clip at evenly spread points, so cuts ripple one at a time. */
const FADE = 0.45;
const STAGGER = 700;
const SPREAD = 2.4;

const range = (n) => [...Array(n).keys()];
const shuffle = (list) => {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
};

/** @param {number[]} firsts clip already shown (as poster) by each tile; @returns {number[][]} clip set per tile */
export function plan(firsts, cols, clipCount, size = 3) {
  const uses = Array(clipCount).fill(0);
  const sets = firsts.map((clip) => (uses[clip]++, [clip]));
  const near = (a, b) =>
    Math.abs((a % cols) - (b % cols)) <= 1 && Math.abs(Math.floor(a / cols) - Math.floor(b / cols)) <= 1;
  const shared = (a, b) => a.filter((clip) => b.includes(clip)).length;
  for (let round = 1; round < size; round++) {
    for (const tile of shuffle(range(sets.length))) {
      const fits = (clip, spaced) =>
        uses[clip] < 2 &&
        !sets[tile].includes(clip) &&
        sets.every((set, other) => other === tile || !set.includes(clip) || (shared(set, sets[tile]) === 0 && !(spaced && near(tile, other))));
      // Shuffle first so the stable sort picks at random among the least used clips.
      const pool = shuffle(range(clipCount)).sort((a, b) => uses[a] - uses[b]);
      const clip = pool.find((c) => fits(c, true)) ?? pool.find((c) => fits(c, false));
      if (clip !== undefined) {
        sets[tile].push(clip);
        uses[clip]++;
      }
    }
  }
  return sets;
}

/** Best of a few random plans: fewest clips repeated in touching tiles (3×3 grids make this hard to avoid). */
function bestPlan(firsts, cols, clipCount) {
  const near = (a, b) =>
    Math.abs((a % cols) - (b % cols)) <= 1 && Math.abs(Math.floor(a / cols) - Math.floor(b / cols)) <= 1;
  const score = (sets) =>
    sets.reduce((sum, set, a) => sum + sets.slice(a + 1).filter((other, i) => near(a, a + 1 + i) && other.some((c) => set.includes(c))).length, 0);
  let best;
  let bestScore = Infinity;
  for (let attempt = 0; attempt < 24 && bestScore > 0; attempt++) {
    const sets = plan(firsts, cols, clipCount);
    const value = score(sets);
    if (value < bestScore) [best, bestScore] = [sets, value];
  }
  return best;
}

class Tile {
  constructor(reel, cell, set, offset) {
    this.reel = reel;
    this.set = set;
    this.at = 0;
    this.queued = -1;
    this.delay = reel.together ? 0 : Math.random() * STAGGER;
    this.offset = reel.together ? 0 : offset;
    this.videos = [0, 1].map(() => {
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.disablePictureInPicture = true;
      video.defaultPlaybackRate = reel.speed;
      video.setAttribute('playsinline', '');
      video.addEventListener('timeupdate', () => {
        if (video !== this.front || !video.duration) return;
        // Fetch the next clip a second in, not at once, so the first frames of the grid load first.
        if (!this.reel.together && video.currentTime > 1 && !this.cutting) this.prime();
        if (!this.reel.together && video.currentTime >= video.duration - FADE) this.cut();
      });
      video.addEventListener('ended', () => video === this.front && !this.reel.together && this.cut());
      cell.append(video);
      return video;
    });
    this.front = this.videos[0];
  }

  get clip() {
    return this.set[this.at];
  }

  get back() {
    return this.front === this.videos[0] ? this.videos[1] : this.videos[0];
  }

  load(video, clip, from = 0) {
    if (video.dataset.clip === String(clip)) return;
    video.dataset.clip = clip;
    video.src = this.reel.url(clip) + (from ? `#t=${from.toFixed(2)}` : '');
    video.playbackRate = this.reel.speed;
  }

  /** Queue and preload the next clip (in 'together' mode the reel chooses it for every tile at once). */
  prime(at = this.next()) {
    this.queued = at;
    if (at >= 0 && at !== this.at) this.load(this.back, this.set[at]);
  }

  /** Load the first clip and resolve once it can play (or fails). */
  ready() {
    this.load(this.front, this.clip, this.offset);
    if (this.front.readyState >= 3) return Promise.resolve();
    return new Promise((done) => {
      this.front.addEventListener('canplay', done, { once: true });
      this.front.addEventListener('error', done, { once: true });
    });
  }

  /** Index in the set of the next clip that is not on screen elsewhere, or -1. */
  next() {
    const busy = this.reel.showing(this);
    for (let step = 1; step < this.set.length; step++) {
      const at = (this.at + step) % this.set.length;
      if (!busy.has(this.set[at])) return at;
    }
    return -1;
  }

  play() {
    if (this.running) return;
    this.running = true;
    const start = () => {
      this.timer = 0;
      this.delay = 0;
      this.load(this.front, this.clip, this.offset);
      this.front
        .play()
        .then(() => this.front.classList.add('is-on'))
        .catch((error) => this.reel.refused(error));
    };
    if (this.delay) this.timer = setTimeout(start, this.delay);
    else start();
  }

  pause() {
    this.running = false;
    clearTimeout(this.timer);
    this.timer = 0;
    this.videos.forEach((video) => video.pause());
  }

  cut() {
    if (this.cutting || !this.running) return;
    let at = this.queued;
    if (!this.reel.together && (at < 0 || this.reel.showing(this).has(this.set[at]))) at = this.next();
    this.queued = -1;
    if (at < 0 || at === this.at) {
      // No free clip in this set: run this one again (from the top, in step with the others when cutting together).
      if (this.reel.together) this.front.currentTime = 0;
      if (this.reel.together || this.front.ended) this.front.play().catch(() => {});
      return;
    }
    this.cutting = true;
    const previous = this.front;
    const incoming = this.back;
    const from = this.at;
    this.at = at;
    this.load(incoming, this.clip);
    incoming.currentTime = 0;
    incoming
      .play()
      .then(() => {
        this.front = incoming;
        incoming.classList.add('is-on', 'is-top');
        previous.classList.remove('is-top');
        setTimeout(() => {
          previous.classList.remove('is-on');
          previous.pause();
          this.cutting = false;
        }, FADE * 1000 + 60);
      })
      .catch(() => {
        // Paused mid-cut: the outgoing clip is still in front.
        this.at = from;
        this.cutting = false;
      });
  }

  stop() {
    this.pause();
    this.videos.forEach((video) => {
      video.removeAttribute('src');
      video.load();
      video.remove();
    });
  }
}

class LurenReel extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-reel-clips]');
    try {
      this.clips = JSON.parse(data?.textContent || '[]');
    } catch {
      this.clips = [];
    }
    this.toggle = this.querySelector('[data-reel-toggle]');
    this.tiles = [];
    this.speed = Math.min(1, Math.max(0.5, Number(this.dataset.speed) / 100 || 1));
    this.together = this.dataset.change !== 'staggered';
    if (this.clips.length < 2) return;

    this.controller = new AbortController();
    const { signal } = this.controller;
    this.held = matchMedia('(prefers-reduced-motion: reduce)').matches || Boolean(navigator.connection?.saveData);
    this.inView = true;
    this.toggle?.addEventListener(
      'click',
      () => {
        this.held = !this.held;
        this.sync();
      },
      { signal }
    );
    document.addEventListener('visibilitychange', () => this.sync(), { signal });
    this.renditions().then(() => {
      if (signal.aborted) return;
      this.viewObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.sync();
      });
      this.viewObserver.observe(this);
      this.sizeObserver = new ResizeObserver(() => this.layout());
      this.sizeObserver.observe(this);
      this.layout();
    });
  }

  /** Liquid lists only each clip's 1080p file; the 480p and 720p ones come from a map keyed by the CDN hash. */
  async renditions() {
    if (!this.dataset.renditions) return;
    try {
      const response = await fetch(this.dataset.renditions, { signal: AbortSignal.timeout(2500) });
      const map = await response.json();
      this.clips.forEach((clip) => Object.assign(clip, map[clip.l?.match(/\/vp\/(\w+)\//)?.[1]]));
    } catch {
      // Without the map every tile plays the 1080p file.
    }
  }

  /** Smallest rendition that still covers the tile: 480p on phones, 720p on most desktops. */
  url(clip) {
    const { s, m, l } = this.clips[clip];
    return (this.pixels <= 400 ? s : this.pixels <= 900 ? m : l) || m || s || l;
  }

  /** Clips on screen in tiles other than `self`. */
  showing(self) {
    return new Set(this.tiles.filter((tile) => tile !== self).map((tile) => tile.clip));
  }

  /** 'together': choose every tile's next clip at once so that as many tiles as possible change and no clip shows
      twice. Bipartite matching (tiles to the other clips of their sets); a tile left over keeps its own clip, and
      whoever had taken that clip looks again. */
  plan() {
    const tiles = shuffle([...this.tiles]);
    const owner = new Map();
    const choice = new Map();
    const claim = (tile, seen) => {
      for (const at of shuffle(range(tile.set.length).filter((i) => i !== tile.at))) {
        const clip = tile.set[at];
        if (seen.has(clip)) continue;
        seen.add(clip);
        const holder = owner.get(clip);
        if (!holder || claim(holder, seen)) {
          owner.set(clip, tile);
          choice.set(tile, at);
          return true;
        }
      }
      return false;
    };
    tiles.forEach((tile) => claim(tile, new Set()));
    const keepers = tiles.filter((tile) => !choice.has(tile));
    while (keepers.length) {
      const tile = keepers.pop();
      const holder = owner.get(tile.clip);
      if (holder && holder !== tile) {
        choice.delete(holder);
        if (!claim(holder, new Set([tile.clip]))) keepers.push(holder);
      }
      owner.set(tile.clip, tile);
      choice.set(tile, tile.at);
    }
    this.tiles.forEach((tile) => tile.prime(choice.get(tile)));
    this.planned = true;
  }

  /** Shared clock for 'together': every tile cuts on the same beat, one clip length (less the fade) apart. */
  beat() {
    if (this.clock) return;
    const lead = () => this.tiles[0]?.front;
    const length = () => (((lead()?.duration || 3) - FADE) / this.speed) * 1000;
    const tick = () => {
      this.clock = setTimeout(tick, length());
      if (!this.planned) this.plan();
      this.planned = false;
      this.tiles.forEach((tile) => tile.cut());
      // Choose and preload the next round a second in, once this round's clips are playing.
      this.planner = setTimeout(() => this.plan(), 1000);
    };
    if (!this.planned) this.planner = setTimeout(() => this.plan(), 1000);
    // After a pause the clips are mid-way: the first beat waits only for what is left of them.
    this.clock = setTimeout(tick, Math.max(0, length() - ((lead()?.currentTime || 0) / this.speed) * 1000));
  }

  rest() {
    clearTimeout(this.clock);
    clearTimeout(this.planner);
    this.clock = 0;
  }

  layout() {
    const cells = [...this.querySelectorAll('.luren-reel__tile')].filter((cell) => cell.offsetParent !== null);
    if (!cells.length) return;
    const cols = parseInt(getComputedStyle(this).getPropertyValue('--reel-cols'), 10) || 4;
    this.pixels = cells[0].offsetWidth * Math.min(devicePixelRatio || 1, 2);
    const key = `${cells.length}/${cols}`;
    if (key === this.key) return;
    this.key = key;
    this.rest();
    this.warm = false;
    this.tiles.forEach((tile) => tile.stop());
    const firsts = cells.map((cell) => Number(cell.dataset.clip) % this.clips.length);
    const sets = bestPlan(firsts, cols, this.clips.length);
    const phases = shuffle(range(cells.length));
    this.tiles = cells.map((cell, i) => new Tile(this, cell, sets[i], (phases[i] / cells.length) * SPREAD));
    this.sync();
  }

  sync() {
    const run = !this.held && this.inView && !document.hidden;
    if (run && this.together && !this.warm) {
      // First start: wait (up to 3s) until every first clip can play, then start them on the same frame.
      this.warm = true;
      const tiles = this.tiles;
      Promise.race([Promise.all(tiles.map((tile) => tile.ready())), new Promise((done) => setTimeout(done, 3000))]).then(
        () => tiles === this.tiles && this.sync()
      );
    } else {
      this.tiles.forEach((tile) => (run ? tile.play() : tile.pause()));
      if (this.together) run ? this.beat() : this.rest();
    }
    if (!this.toggle) return;
    this.toggle.hidden = false;
    this.toggle.dataset.state = this.held ? 'paused' : 'playing';
    this.toggle.setAttribute('aria-label', this.held ? this.dataset.playLabel : this.dataset.pauseLabel);
  }

  /** Autoplay refused (for example Low Power Mode): keep the stills and offer the play control. */
  refused(error) {
    if (error?.name !== 'NotAllowedError' || this.held) return;
    this.held = true;
    this.sync();
  }

  disconnectedCallback() {
    this.rest();
    this.controller?.abort();
    this.viewObserver?.disconnect();
    this.sizeObserver?.disconnect();
    this.tiles?.forEach((tile) => tile.stop());
    this.tiles = [];
    this.key = '';
  }
}

if (!customElements.get('luren-reel')) customElements.define('luren-reel', LurenReel);
